/**
 * Fibration, slice-category, and double-category diagrams.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useId } from "react";
import { GRAY } from "../../../design-system/tokens.js";
import { DIAGRAM_MONO, STROKE, SUBTLE, LABEL, ArrowDefs, ObjectNode, Arrow } from "./primitives.js";

/**
 * Fibration diagram: total category E above base category B with projection
 * functor π. Shows a cartesian lift f̃ above a base morphism f. When
 * mode="fails", the lift is dashed with ✗ (the seam).
 */
export function FibrationDiagram({
  mode = "lifts",
  accent = STROKE,
  ariaLabel = "Fibration diagram with projection π : E → B and cartesian lift",
}: {
  readonly mode?: "lifts" | "fails";
  readonly accent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  const fails = mode === "fails";
  return (
    <svg
      viewBox="0 0 340 240"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />

      {/* --- E (total category) --- */}
      <text x={20} y={30} fontSize="15" fontWeight={700} fill={LABEL} fontFamily={DIAGRAM_MONO}>
        E
      </text>
      <ObjectNode cx={100} cy={60} label="e" subscript="A" accent={accent} candidate={fails} />
      <ObjectNode cx={260} cy={60} label="e" subscript="B" accent={accent} />
      <Arrow
        x1={116}
        y1={60}
        x2={244}
        y2={60}
        label="f̃"
        markerId={markerId}
        color={fails ? GRAY[500] : accent}
        variant={fails ? "dashed" : "solid"}
        forbidden={fails}
      />

      {/* --- projection π arrows --- */}
      <Arrow
        x1={100}
        y1={76}
        x2={100}
        y2={158}
        label=""
        markerId={markerId}
        color={SUBTLE}
        variant="dashed"
      />
      <Arrow
        x1={260}
        y1={76}
        x2={260}
        y2={158}
        label=""
        markerId={markerId}
        color={SUBTLE}
        variant="dashed"
      />
      <text
        x={170}
        y={130}
        textAnchor="middle"
        fontSize="14"
        fontWeight={600}
        fill={SUBTLE}
        fontFamily={DIAGRAM_MONO}
      >
        π
      </text>

      {/* --- B (base category) --- */}
      <text x={20} y={180} fontSize="15" fontWeight={700} fill={LABEL} fontFamily={DIAGRAM_MONO}>
        B
      </text>
      <ObjectNode cx={100} cy={180} label="A" accent={accent} />
      <ObjectNode cx={260} cy={180} label="B" accent={accent} />
      <Arrow x1={116} y1={180} x2={244} y2={180} label="f" markerId={markerId} color={accent} />
    </svg>
  );
}

/**
 * Slice category diagram: objects X, Y with morphisms to base A (valid slice
 * objects), plus Z with no morphism to A (dashed forbidden arrow with ✗).
 */
export function SliceCategoryDiagram({
  accent = STROKE,
  ariaLabel = "Slice category C/A — X→A and Y→A exist; Z→A is absent",
}: {
  readonly accent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  return (
    <svg
      viewBox="0 0 360 200"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />

      {/* base object A */}
      <ObjectNode cx={180} cy={170} label="A" accent={accent} />

      {/* valid slice objects */}
      <ObjectNode cx={70} cy={50} label="X" accent={accent} />
      <Arrow x1={82} y1={62} x2={168} y2={158} markerId={markerId} color={accent} />

      <ObjectNode cx={180} cy={50} label="Y" accent={accent} />
      <Arrow x1={180} y1={66} x2={180} y2={154} markerId={markerId} color={accent} />

      {/* forbidden: Z has no morphism to A */}
      <ObjectNode cx={290} cy={50} label="Z" accent={GRAY[500]} candidate />
      <Arrow
        x1={278}
        y1={62}
        x2={192}
        y2={158}
        markerId={markerId}
        color={GRAY[500]}
        variant="dashed"
        forbidden
      />
    </svg>
  );
}

/**
 * Double category diagram: 2×2 grid with horizontal arrows (data flow),
 * vertical arrows (authority flow), and 2-cell squares. One square is
 * filled (composable); the other has ✗ (SEPARATE seam — empty 2-cell).
 */
export function DoubleCategoryDiagram({
  accent = STROKE,
  ariaLabel = "Double category with composable square and empty SEPARATE seam square",
}: {
  readonly accent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  return (
    <svg
      viewBox="0 0 400 220"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />

      {/* --- Left square (composable) --- */}
      <ObjectNode cx={50} cy={50} label="A" accent={accent} />
      <ObjectNode cx={170} cy={50} label="B" accent={accent} />
      <ObjectNode cx={50} cy={170} label="C" accent={accent} />
      <ObjectNode cx={170} cy={170} label="D" accent={accent} />

      {/* horizontal arrows (data flow) */}
      <Arrow x1={66} y1={50} x2={154} y2={50} label="h" markerId={markerId} color={accent} />
      <Arrow x1={66} y1={170} x2={154} y2={170} label="h′" markerId={markerId} color={accent} />
      {/* vertical arrows (authority flow) */}
      <Arrow x1={50} y1={66} x2={50} y2={154} label="v" markerId={markerId} color={GRAY[600]} />
      <Arrow x1={170} y1={66} x2={170} y2={154} label="v′" markerId={markerId} color={GRAY[600]} />
      {/* filled 2-cell */}
      <rect
        x={80}
        y={85}
        width={60}
        height={50}
        rx={6}
        fill={`${accent}1A`}
        stroke={accent}
        strokeWidth={1}
      />
      <text
        x={110}
        y={115}
        textAnchor="middle"
        fontSize="13"
        fontWeight={600}
        fill={accent}
        fontFamily={DIAGRAM_MONO}
      >
        α
      </text>

      {/* --- Right square (SEPARATE seam — empty) --- */}
      <ObjectNode cx={230} cy={50} label="B" accent={accent} />
      <ObjectNode cx={350} cy={50} label="E" accent={GRAY[500]} />
      <ObjectNode cx={230} cy={170} label="D" accent={accent} />
      <ObjectNode cx={350} cy={170} label="F" accent={GRAY[500]} />

      {/* horizontal arrows */}
      <Arrow
        x1={246}
        y1={50}
        x2={334}
        y2={50}
        label="h″"
        markerId={markerId}
        color={GRAY[500]}
        variant="dashed"
      />
      <Arrow
        x1={246}
        y1={170}
        x2={334}
        y2={170}
        label="h‴"
        markerId={markerId}
        color={GRAY[500]}
        variant="dashed"
      />
      {/* vertical arrows */}
      <Arrow x1={230} y1={66} x2={230} y2={154} label="v′" markerId={markerId} color={GRAY[600]} />
      <Arrow x1={350} y1={66} x2={350} y2={154} label="v″" markerId={markerId} color={GRAY[600]} />
      {/* empty 2-cell (seam) */}
      <rect
        x={260}
        y={85}
        width={60}
        height={50}
        rx={6}
        fill="none"
        stroke={GRAY[400]}
        strokeWidth={1}
        strokeDasharray="4 3"
      />
      <text x={290} y={117} textAnchor="middle" fontSize="20" fontWeight={700} fill={GRAY[600]}>
        ✗
      </text>
    </svg>
  );
}
